/**
 * Вход через Telegram на сайте (браузер, не Mini App): имя бота приходит от сервера (`GET /config`, env
 * `TG_BOT_USERNAME`), виджет Telegram Login отдаёт подписанные данные, сервер проверяет подпись и сливает гостевой
 * прогресс (`authTelegramLogin`). Без имени бота кнопки нет и скрипт telegram.org не грузится.
 */
import type { AuthTelegramLoginRequest, GameMode } from "@newfungame/contracts";
import { ApiError } from "@newfungame/contracts";
import type { ApiTransport } from "../api";
import { getGameMode } from "./mode";
import { registerTelegramLogin } from "./slots";
import { appStore, type AppState, type Store } from "./store";

/** Имя бота Telegram: 5–32 символа, латиница, цифры, подчёркивание. */
const BOT_NAME = /^[A-Za-z0-9_]{5,32}$/;

/** Домен: имя хоста без схемы и порта. */
const DOMAIN = /^[a-z0-9.-]{3,253}$/i;

export interface LoginConfig {
  bot: string | null;
  /** Домен, привязанный к боту в BotFather; `null` — сервер его не назвал. */
  domain: string | null;
}

/** Конфиг входа с сервера (`GET /config`): имя бота и домен виджета. */
export async function fetchLoginConfig(fetchImpl: typeof fetch = fetch): Promise<LoginConfig> {
  try {
    const res = await fetchImpl("/config");
    if (!res.ok) return { bot: null, domain: null };
    const body = (await res.json()) as { telegramBot?: unknown; telegramLoginDomain?: unknown };
    const bot = typeof body.telegramBot === "string" && BOT_NAME.test(body.telegramBot) ? body.telegramBot : null;
    const domain =
      typeof body.telegramLoginDomain === "string" && DOMAIN.test(body.telegramLoginDomain)
        ? body.telegramLoginDomain.toLowerCase()
        : null;
    return { bot, domain };
  } catch {
    return { bot: null, domain: null };
  }
}

/** Имя бота из конфига сервера; `null` — вход через Telegram не настроен (или сервер недоступен). */
export async function fetchLoginBot(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  return (await fetchLoginConfig(fetchImpl)).bot;
}

/**
 * Виджет Telegram Login при чужом домене рисует внутри iframe «Bot domain invalid», и прочитать это нельзя. Поэтому
 * показываем его, только когда уверены: сервер назвал домен бота и мы на нём. Домен не назван — прежнее поведение в
 * обычной игре, но не в бете.
 */
export function loginWidgetAllowed(cfg: LoginConfig, hostname: string, mode: GameMode): boolean {
  if (!cfg.bot) return false;
  const host = hostname.toLowerCase();
  if (cfg.domain) return host === cfg.domain;
  return mode !== "beta";
}

/**
 * Данные виджета → сервер → игрок из ответа (прогресс слит с аккаунтом Telegram). Забежка может смениться — бой
 * начинается заново с неё (`runEpoch`). Ошибка входа игру не трогает: гость остаётся гостем, кнопка остаётся.
 */
export async function loginWithTelegram(
  api: ApiTransport,
  data: AuthTelegramLoginRequest,
  store: Store<AppState> = appStore,
): Promise<boolean> {
  try {
    const { landing } = store.get();
    const r = await api.call("authTelegramLogin", { ...data, ...(landing ? { landing } : {}) });
    store.set((s) => ({
      ...s,
      player: r.state,
      boostOffer: null,
      runEpoch: s.runEpoch + 1,
      auth: {
        status: "ready",
        guest: r.state.player.telegramId === null,
        created: r.created,
        error: null,
        history: r.history,
      },
    }));
    return true;
  } catch (e) {
    store.set((s) => ({ ...s, auth: { ...s.auth, error: e instanceof ApiError ? e.code : "network" } }));
    return false;
  }
}

/** Подключить кнопку входа: только в браузере и только если сервер знает имя бота. */
export async function setupTelegramLogin(api: ApiTransport, miniApp: boolean): Promise<void> {
  if (miniApp) return;
  const cfg = await fetchLoginConfig();
  if (cfg.bot && loginWidgetAllowed(cfg, location.hostname, getGameMode()))
    registerTelegramLogin({ bot: cfg.bot, onAuth: (data) => void loginWithTelegram(api, data) });
}
